import React from 'react';
import { Box, Container, Typography, useTheme } from '@mui/material';
import { motion } from 'motion/react';
import { SvgIconComponent } from '@mui/icons-material';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import MyLocationRoundedIcon from '@mui/icons-material/MyLocationRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import { content, ExpertiseItem } from '../../data/content';
import { useAnimationVariants } from '../../hooks/useAnimationVariants';
import { useTiltEffect } from '../../hooks/useTiltEffect';
import { useParallax } from '../../hooks/useParallax';

const MotionBox = motion.create(Box);

const ICONS: Record<string, SvgIconComponent> = {
  architecture: LayersRoundedIcon,
  state:        HubRoundedIcon,
  performance:  SpeedRoundedIcon,
  security:     ShieldRoundedIcon,
  location:     MyLocationRoundedIcon,
  delivery:     RocketLaunchRoundedIcon,
  web:          LanguageRoundedIcon,
};

const SPANS    = [2, 1, 1, 2, 2, 1];
const GRADIENTS = [
  'linear-gradient(135deg, #818CF8, #a78bfa)',
  'linear-gradient(135deg, #a78bfa, #f472b6)',
  'linear-gradient(135deg, #34d399, #818CF8)',
  'linear-gradient(135deg, #818CF8, #34d399)',
  'linear-gradient(135deg, #f472b6, #818CF8)',
  'linear-gradient(135deg, #34d399, #a78bfa)',
];

// Each card is its own component so hooks run per-instance (not inside .map)
interface ExpertiseCardProps {
  item: ExpertiseItem;
  gradient: string;
  span: number;
  index: number;
}

const ExpertiseCard: React.FC<ExpertiseCardProps> = ({ item, gradient, span, index }) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const tilt   = useTiltEffect({ maxRotation: 7 });
  const { card } = useAnimationVariants();

  return (
    <MotionBox
      variants={card}
      sx={{ gridColumn: { md: `span ${span}` } }}
    >
      <Box
        ref={tilt.ref}
        onMouseMove={tilt.handleMouseMove}
        onMouseLeave={tilt.handleMouseLeave}
        component={motion.div}
        whileHover={{ y: -6, scale: 1.01 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        style={{ rotateX: tilt.rotateX, rotateY: tilt.rotateY, transformStyle: 'preserve-3d' } as any}
        sx={{ height: '100%', perspective: '1000px' }}
      >
        <Box
          sx={{
            height: '100%', p: { xs: 3.5, md: 4 }, borderRadius: '18px',
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper', position: 'relative', overflow: 'hidden',
            cursor: 'default',
            transition: 'border-color 280ms ease, box-shadow 280ms ease',
            '&:hover': {
              borderColor: `${theme.palette.primary.main}44`,
              boxShadow: `0 0 40px ${theme.palette.primary.main}18, 0 8px 32px rgba(0,0,0,0.25)`,
            },
            '&::before': {
              content: '""', position: 'absolute', top: 0, left: 0, right: 0,
              height: '3px', background: gradient, borderRadius: '3px 3px 0 0', opacity: 0.9,
            },
          }}
        >
          {/* Dynamic mouse-follow spotlight */}
          <Box
            component={motion.div}
            style={{
              background: `radial-gradient(circle at ${tilt.glowX}% ${tilt.glowY}%, rgba(129,140,248,0.09) 0%, transparent 60%)`,
            } as any}
            sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', borderRadius: '16px', zIndex: 0 }}
          />

          {/* Large decorative number */}
          <Typography sx={{ position: 'absolute', right: 16, bottom: -8, fontFamily: '"Space Grotesk", sans-serif', fontSize: '5rem', fontWeight: 800, color: theme.palette.primary.main, opacity: isDark ? 0.05 : 0.04, lineHeight: 1, userSelect: 'none', pointerEvents: 'none', zIndex: 0 }}>
            {String(index + 1).padStart(2, '0')}
          </Typography>

          {/* Icon box */}
          <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: '12px', background: gradient, mb: 2.5, color: '#fff', boxShadow: `0 4px 16px ${theme.palette.primary.main}30`, position: 'relative', zIndex: 1 }}>
            {(() => {
              const Icon = ICONS[item.icon] ?? LayersRoundedIcon;
              return <Icon sx={{ fontSize: 24 }} aria-hidden />;
            })()}
          </Box>

          <Typography variant={span === 2 ? 'h5' : 'h6'} sx={{ fontWeight: 700, color: 'text.primary', mb: 1.5, fontSize: span === 2 ? { xs: '1.0625rem', md: '1.175rem' } : '1rem', position: 'relative', zIndex: 1 }}>
            {item.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, fontSize: '0.9375rem', position: 'relative', zIndex: 1 }}>
            {item.desc}
          </Typography>
        </Box>
      </Box>
    </MotionBox>
  );
};

const Expertise: React.FC = () => {
  const { overline, title, items } = content.expertise;
  const { fadeUp, stagger } = useAnimationVariants();
  const headerParallax = useParallax({ outputRange: [50, -50] });
  const gridParallax   = useParallax({ outputRange: [25, -25] });

  return (
    <Box id="expertise" sx={{ py: { xs: 10, md: 14 }, bgcolor: 'background.paper', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', bottom: '-15%', right: '-8%', width: '40%', paddingBottom: '40%', borderRadius: '50%', background: 'radial-gradient(circle, #a78bfa14 0%, transparent 70%)', pointerEvents: 'none', filter: 'blur(60px)', zIndex: 0 }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header with parallax */}
        <Box ref={headerParallax.ref}>
          <MotionBox style={{ y: headerParallax.y }}>
            <MotionBox variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} sx={{ textAlign: 'center', mb: { xs: 6, md: 9 } }}>
              <MotionBox variants={fadeUp}>
                <Typography variant="overline" sx={{ color: 'primary.main', fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', letterSpacing: '0.14em', mb: 1.5, display: 'block' }}>
                  {overline}
                </Typography>
              </MotionBox>
              <MotionBox variants={fadeUp}>
                <Typography variant="h2" component="h2" sx={{ color: 'text.primary', maxWidth: 620, mx: 'auto' }}>
                  {title}
                </Typography>
              </MotionBox>
            </MotionBox>
          </MotionBox>
        </Box>

        {/* Bento grid with parallax */}
        <Box ref={gridParallax.ref}>
          <MotionBox style={{ y: gridParallax.y }}>
            <MotionBox
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-60px' }}
              sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: { xs: 2, md: 2.5 } }}
            >
              {items.map((item, i) => (
                <ExpertiseCard key={item.title} item={item} gradient={GRADIENTS[i]} span={SPANS[i]} index={i} />
              ))}
            </MotionBox>
          </MotionBox>
        </Box>
      </Container>
    </Box>
  );
};

export default Expertise;
